import { Heart, FileDown, GitCompareArrows } from 'lucide-react';
import CartPicker from '../common/CartPicker';
import type { Product } from '../../types/product';
import { useAuth } from '../../hooks/useAuth';
import { useFavoriteActions } from '../../hooks/useFavoriteActions';
import { useCartActions } from '../../hooks/useCartActions';
import { useCompareActions } from '../../hooks/useCompareActions';
import { useSettingsStore } from '../../stores/useSettingsStore';
import AnimatedActionButton from '../common/AnimatedActionButton';
import HighlightedText from '../common/HighlightedText';
import FavoriteGroupPicker from '../common/FavoriteGroupPicker';
import NewBadge from './NewBadge';
import NewStudyBadge from './NewStudyBadge';

import { useTranslation } from '../../i18n';

interface ProductCardProps {
  product: Product;
  onClick: () => void;
  searchTerm?: string;
}

export default function ProductCard({ product, onClick, searchTerm = '' }: ProductCardProps) {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { toggleFavorite, isFavorite } = useFavoriteActions(isLoggedIn);
  const { addToCart, removeFromCart, isInCart } = useCartActions(isLoggedIn);
  const { toggleCompare, isInCompare } = useCompareActions(isLoggedIn);
  const { favoriteGroupsEnabled, multiCartEnabled } = useSettingsStore();

  const productId = String(product.id);
  const favorited = isFavorite(productId);
  const inCart = isInCart(product.id);
  const inCompare = isInCompare(productId);
  const categoryName = product.category?.name ?? '';

  return (
    <div
      className="group relative flex flex-col rounded-xl hover:scale-[1.02] transition-all duration-200 cursor-pointer overflow-hidden bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800"
      onClick={onClick}
    >
      {/* Image area */}
      <div className="relative h-32 bg-gray-50 dark:bg-gray-700/30 flex items-center justify-center overflow-hidden">
        {product.logoUrl ? (
          <img
            src={product.logoUrl.replace(/^https?:\/\/[^/]+/, '')}
            alt={product.name}
            className="max-h-full max-w-full object-contain p-3"
            loading="lazy"
          />
        ) : (
          <span className="text-3xl font-bold text-gray-300 dark:text-gray-600">
            {product.name.charAt(0)}
          </span>
        )}

        {(product.isNew || product.hasNewStudy) && (
          <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
            {product.isNew && <NewBadge />}
            {product.hasNewStudy && <NewStudyBadge />}
          </div>
        )}
      </div>

      {/* Category bar */}
      {categoryName && (
        <div className="bg-gray-100 dark:bg-gray-700/50 px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
          <HighlightedText text={categoryName} term={searchTerm} />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col flex-1 p-4 gap-2">
        {/* Name & Partner */}
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm">
            <HighlightedText text={product.name} term={searchTerm} />
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            <HighlightedText text={product.partner?.name} term={searchTerm} />
          </p>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-around bg-gray-50/50 dark:bg-gray-900/30 px-4 py-2">
        {favoriteGroupsEnabled ? (
          <FavoriteGroupPicker
            productId={productId}
            onSimpleToggle={(e) => { e.stopPropagation(); toggleFavorite(productId); }}
            align="right"
          />
        ) : (
          <AnimatedActionButton
            title={favorited ? t.actions.removeFromFavorites : t.actions.addToFavorites}
            onClick={(e) => { e.stopPropagation(); toggleFavorite(productId); }}
            icon={<Heart className={`w-4 h-4 transition-colors ${favorited ? 'fill-red-500 text-red-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400'}`} />}
          />
        )}
        {multiCartEnabled ? (
          <>
            <span className="md:hidden">
              <CartPicker productId={product.id} direction="up" />
            </span>
            <span className="hidden md:inline">
              <AnimatedActionButton
                title={inCart ? t.actions.removeFromCart : t.actions.addToCart}
                onClick={(e) => { e.stopPropagation(); if (inCart) removeFromCart(product.id); else addToCart(product.id); }}
                icon={<FileDown className={`w-4 h-4 transition-colors ${inCart ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-vivatis-600 dark:group-hover:text-vivatis-400'}`} />}
              />
            </span>
          </>
        ) : (
          <AnimatedActionButton
            title={inCart ? t.actions.removeFromCart : t.actions.addToCart}
            onClick={(e) => { e.stopPropagation(); if (inCart) removeFromCart(product.id); else addToCart(product.id); }}
            icon={<FileDown className={`w-4 h-4 transition-colors ${inCart ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-vivatis-600 dark:group-hover:text-vivatis-400'}`} />}
          />
        )}
        <AnimatedActionButton
          title={inCompare ? t.actions.removeFromComparison : t.actions.addToComparison}
          onClick={(e) => { e.stopPropagation(); toggleCompare(productId); }}
          icon={<GitCompareArrows className={`w-4 h-4 transition-colors ${inCompare ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-vivatis-600 dark:group-hover:text-vivatis-400'}`} />}
        />
      </div>
    </div>
  );
}
